import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Plus, Trash2,
  User, ChevronDown,
  ChevronUp, Search, CheckSquare, Square, AlertTriangle,
  CheckCircle, Loader, X
} from 'lucide-react'
import PageIdentifier from '../shared/PageIdentifier'
import CostPanel from '../shared/CostPanel'
import BillingActionBar from '../shared/BillingActionBar'
import PdfViewerModal from '../shared/PdfViewerModal'
import PaymentConfirmModal from '../billing/PaymentConfirmModal'
import PdfService from '../../services/PdfService'
import EmailSendService from '../../services/EmailSendService'
import WhatsAppLinkService from '../../services/WhatsAppLinkService'
import SCREEN_IDS from '../../utils/screenIds'
import { getGoldPurityFactor, applyExchangeRate, stoneUnitLabel } from '../../utils/calculations'
import {
  calculateAcquisitionCost, applyKundanGoldCostDeduction, isKundanItemCode, calculateCostPanelResult,
  calculatePurityGoldRate, calculateGoldValue, calculateWastageAmount,
  calculateMakingAmount, calculateTotalGoldAmount, calculateCertificationCharges,
  calculateJewelValue, calculateUSDGrandTotal, calculateINRGrandTotal,
} from '../../shared/utils/jewelryCalculations'
import HistoricalRateService from '../../services/HistoricalRateService'
import usePermissions from '../../hooks/usePermissions'

// ─── helpers ───────────────────────────────────────────────────────────────

const blurOnEnter = (e) => { if (e.key === 'Enter') e.target.blur() }

const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const fmtCurrency = (val, currency) => {
  const n = parseFloat(val) || 0
  return currency === 'USD'
    ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ─── calcItemTotal ──────────────────────────────────────────────────────────
// Uses the shared jewelryCalculations pipeline so a line total here is
// bit-for-bit identical to what US Billing / Indian Billing (and their
// estimates) compute for the same item:
//   purity gold rate → gold value → total gold amount (ceil)
//   → jewel value (ceil gold + ceil stone + cert) → USD/INR grand total.
// Gold rate is the INR 24K per-gram rate; stone sale_price and makingCharge
// are INR. exchangeRate is INR per 1 USD (already adjustment-applied).
// Certification: diamondCarats × certChargePerCarat, only when the item is
// flagged certified — all three fields are set on the item at fetch time.

const calcItemTotal = (item, exchangeRate, gstRate, usdTaxRate) => {
  const isUSD = item.currency === 'USD'
  const exRate = parseFloat(exchangeRate) || 1
  const purityFactor = getGoldPurityFactor(item.purity)
  const grossWeight = parseFloat(item.grossWeight) || 0
  const stoneWt = parseFloat(item.stoneWeight) || 0
  const netWeight = grossWeight > 0
    ? Math.max(0, grossWeight - stoneWt)
    : parseFloat(item.netWeight) || 0

  // ── Gold (INR) ──
  const goldPrice = calculatePurityGoldRate(item.goldRate, item.purity)   // purity-adjusted ₹/g
  const fineWeight = netWeight * purityFactor
  const goldValue = calculateGoldValue(goldPrice, netWeight)
  const wastageAmt = calculateWastageAmount(netWeight, item.wastagePercent, goldPrice)
  const makingAmt = calculateMakingAmount(netWeight, item.makingCharge)
  const totalGoldAmountINR = calculateTotalGoldAmount(goldValue, wastageAmt, makingAmt)

  // ── Stones (INR) — weight × sale_price, native unit (carat rate × carats) ──
  const stoneTotalINR = (item.stoneDetails && item.stoneDetails.length > 0)
    ? item.stoneDetails.reduce((acc, s) =>
        acc + (parseFloat(s.weight) || 0) * (parseFloat(s.sale_price ?? s.rate) || 0), 0)
    : parseFloat(item.stoneTotal) || 0

  // ── Certification (INR) ──
  const certAmtINR = calculateCertificationCharges(
    item.diamondCarats, item.certChargePerCarat, item.certificationRequired
  )

  // ── Jewel value + grand total ──
  const jewelValueINR = calculateJewelValue(totalGoldAmountINR, stoneTotalINR, certAmtINR)
  const taxRate = isUSD
    ? (parseFloat(item.taxRate) || parseFloat(usdTaxRate) || 0)
    : (parseFloat(item.taxRate) || parseFloat(gstRate) || 3)

  let grandTotalINR, taxAmtINR, grandTotalUSD, taxAmtUSD
  if (isUSD) {
    const r = calculateUSDGrandTotal(jewelValueINR, taxRate, exRate)
    grandTotalINR = r.grandTotalINR
    taxAmtINR = r.taxAmountINR
    grandTotalUSD = r.grandTotalUSD
    taxAmtUSD = r.taxAmountUSD
  } else {
    const r = calculateINRGrandTotal(jewelValueINR, taxRate)
    grandTotalINR = r.grandTotal
    taxAmtINR = r.gstAmount
    grandTotalUSD = grandTotalINR / exRate
    taxAmtUSD = taxAmtINR / exRate
  }

  const toDisplay = (inrVal) => isUSD ? inrVal / exRate : inrVal

  return {
    netWeight:   netWeight.toFixed(3),
    stoneWeight: stoneWt.toFixed(3),
    fineWeight:  fineWeight.toFixed(3),
    goldValue:   toDisplay(goldValue).toFixed(2),
    wastageAmt:  toDisplay(wastageAmt).toFixed(2),
    makingAmt:   toDisplay(makingAmt).toFixed(2),
    stoneTotal:  toDisplay(stoneTotalINR).toFixed(2),
    certAmt:     toDisplay(certAmtINR).toFixed(2),
    subtotal:    toDisplay(jewelValueINR).toFixed(2),
    taxAmt:      (isUSD ? taxAmtUSD : taxAmtINR).toFixed(2),
    total:       (isUSD ? grandTotalUSD : grandTotalINR).toFixed(2),
    // Raw INR values (used for saving to DB)
    goldValueINR:        goldValue.toFixed(2),
    wastageAmtINR:       wastageAmt.toFixed(2),
    makingAmtINR:        makingAmt.toFixed(2),
    totalGoldAmountINR:  totalGoldAmountINR.toFixed(2),
    stoneTotalINR:       stoneTotalINR.toFixed(2),
    certAmtINR:          certAmtINR.toFixed(2),
    subtotalINR:         jewelValueINR.toFixed(2),
    taxAmtINR:           taxAmtINR.toFixed(2),
    grandTotalINR:       grandTotalINR.toFixed(2)
  }
}

const newCartItem = () => ({
  id: Date.now() + Math.random(),
  itemCode: '',
  jewelryName: '',
  purity: '',
  grossWeight: '',
  netWeight: '',
  stoneWeight: '',
  goldRate: '',
  wastagePercent: '',
  makingCharge: '',
  stoneTotal: '',
  taxRate: '',
  currency: 'USD',
  notes: '',
  status: 'idle',        // idle | loading | found | sold | not_found | error
  statusMsg: '',
  stoneDetails: [],
  diamondCarats: 0,          // set at fetch — feeds certification charge
  certChargePerCarat: 0,     // from category (parent fallback)
  certificationRequired: false,
  estimateId: null,      // set after saving
  estimateNumber: '',
  selected: true
})

// ─── LoadEstimateModal ──────────────────────────────────────────────────────

const LoadEstimateModal = ({ onClose, onLoad, lockedCustomer = '' }) => {
  // Once the cart already has an item, only this customer's other estimates
  // are useful (a bill is single-customer) — pre-fill and lock the search so
  // staff land straight on them instead of hitting the mismatch error.
  const [search, setSearch] = useState(lockedCustomer)
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return }
    setLoading(true)
    try {
      // Only loadable (not-yet-billed) estimates — an already-converted one
      // shouldn't be offered again.
      const res = await fetch(`/api/estimates?customer=${encodeURIComponent(q)}&status=active&limit=30`, { credentials: 'include' })
      const data = await res.json()
      setResults(data.success ? data.estimates : [])
    } catch { setResults([]) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 400)
    return () => clearTimeout(t)
  }, [search, doSearch])

  const handleLoad = (est) => {
    const item = newCartItem()
    item.itemCode = est.item_code || ''
    item.jewelryName = est.jewelry_name || ''
    item.purity = est.gold_purity ? String(est.gold_purity) : ''
    item.grossWeight = est.gross_weight ? String(est.gross_weight) : ''
    item.netWeight = est.net_weight ? String(est.net_weight) : ''
    // calcItemTotal derives net = gross − stoneWeight, so carry the estimate's
    // own gross − net; otherwise net (and the gold/VA/making price) = gross.
    item.stoneWeight = est.gross_weight && est.net_weight
      ? String(Math.max(0, parseFloat(est.gross_weight) - parseFloat(est.net_weight)).toFixed(3))
      : ''
    item.goldRate = est.gold_rate ? String(est.gold_rate) : ''
    item.wastagePercent = est.wastage_percent ? String(est.wastage_percent) : ''
    item.makingCharge = est.making_charge_per_gram ? String(est.making_charge_per_gram) : ''
    item.stoneTotal = est.stone_total ? String(est.stone_total) : ''
    item.taxRate = est.tax_rate ? String(est.tax_rate) : ''
    item.currency = est.currency || 'INR'
    item.stoneDetails = est.stone_details || []
    item.status = 'found'
    item.statusMsg = `Loaded from ${est.estimate_number}`
    // Carried through to checkout so the source estimate gets marked
    // converted once this line is actually billed and paid.
    item.estimateId = est.id
    item.estimateNumber = est.estimate_number
    onLoad(item, { name: est.customer_name, phone: est.customer_phone, email: est.customer_email })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Search size={16} /> Load Saved Estimate
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-4">
          {lockedCustomer && (
            <div className="mb-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              This cart already has item(s) for <strong>{lockedCustomer}</strong> — a bill covers one customer
              only. Showing their other estimates; searching for someone else won't load into this cart.
            </div>
          )}
          <input
            autoFocus
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by customer name or phone..."
            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
        <div className="max-h-80 overflow-y-auto px-4 pb-4">
          {loading && <div className="text-center text-gray-500 text-xs py-4">Searching...</div>}
          {!loading && results.length === 0 && search.length >= 2 && (
            <div className="text-center text-gray-400 text-xs py-4">No estimates found</div>
          )}
          {results.map(est => (
            <div
              key={est.id}
              onClick={() => handleLoad(est)}
              className="flex items-center justify-between p-3 border border-slate-200 rounded-lg mb-2 cursor-pointer hover:bg-slate-50 hover:border-slate-300 transition-colors"
            >
              <div>
                <div className="text-sm font-medium">{est.customer_name}</div>
                <div className="text-xs text-gray-500">{est.estimate_number} · {est.item_code || '—'} · {est.jewelry_name || '—'}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-slate-700">{fmtCurrency(est.grand_total, est.currency)}</div>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${est.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {est.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── CartRow ────────────────────────────────────────────────────────────────

const roClass = 'w-full border border-slate-200 rounded px-2 py-1 text-xs bg-slate-50 text-slate-700 text-right'
const edClass = 'w-full border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400 text-right'
const lblClass = 'text-[11px] uppercase tracking-wide text-slate-400 mb-0.5 whitespace-nowrap'

const CartRow = ({ item, onUpdate, onRemove, goldRateINR, exchangeRate, gstRate, usdTaxRate, categories, index, isCostActive, onCostActivate }) => {
  const { hasPermission } = usePermissions()
  const isUser = !hasPermission('estimate-cart', 'edit')
  const [expanded, setExpanded] = useState(false)
  const [showCostPanel, setShowCostPanel] = useState(false)
  const itemCodeRef = useRef(null)
  const hasAutoFilled = useRef(false)

  useEffect(() => {
    if (isUser || !expanded || !isCostActive) {
      setShowCostPanel(false)
      return
    }

    const onDown = (e) => {
      if (e.key === '`') {
        e.preventDefault()
        if (!e.repeat) setShowCostPanel(true)
      }
    }
    const onUp = (e) => { if (e.key === '`') setShowCostPanel(false) }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
    }
  }, [isUser, expanded, isCostActive])

  // item.goldRate is ALWAYS the INR rate — currency is display-only
  const computed = calcItemTotal(item, exchangeRate, gstRate, usdTaxRate)
  const sym = item.currency === 'USD' ? '$' : '₹'

  // Auto-fill goldRate (INR) the first time the rate loads
  useEffect(() => {
    if (!goldRateINR || hasAutoFilled.current) return
    if (!item.goldRate) {
      onUpdate(item.id, { goldRate: String(goldRateINR) })
    }
    hasAutoFilled.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldRateINR])

  // Auto-fill taxRate when rates first arrive — use the correct rate based on currency
  useEffect(() => {
    const defaultRate = item.currency === 'INR'
      ? (parseFloat(gstRate) || 3)
      : (parseFloat(usdTaxRate) || 0)
    if (!defaultRate) return
    if (!item.taxRate) {
      onUpdate(item.id, { taxRate: String(defaultRate) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstRate, usdTaxRate])

  // Recalc stoneTotal whenever stoneDetails rates change
  const stoneDetailsTotal = (details) => {
    return details.reduce((acc, s) => {
      const w = parseFloat(s.weight || 0)
      const r = parseFloat(s.sale_price || s.rate || 0)
      return acc + w * r
    }, 0).toFixed(2)
  }

  const handleStoneRateChange = (idx, val) => {
    const updated = item.stoneDetails.map((s, i) =>
      i === idx ? { ...s, sale_price: val } : s
    )
    onUpdate(item.id, {
      stoneDetails: updated,
      stoneTotal: stoneDetailsTotal(updated)
    })
  }

  const fetchDetails = async (code) => {
    if (!code) return
    onUpdate(item.id, { status: 'loading', statusMsg: '' })
    try {
      const res = await fetch(`/api/jewelry/details/${code.trim().toUpperCase()}`, { credentials: 'include' })
      const data = await res.json()
      if (data && data.code) {
        // Block on any item that isn't In Stock — Sold, Reserved (mid-checkout
        // on another bill), Under Repair, etc. The 409 payload only carries
        // {error, status, code, message}, not the full item, so this must be
        // checked before reading fields (grossWeight, stoneCategoryTotals, ...)
        // that don't exist on that response.
        if (res.status === 409 || data.error === 'item_status') {
          onUpdate(item.id, {
            status: 'sold',
            statusMsg: `⚠️ ${data.message || `${data.status || 'Unavailable'}`}`,
            itemCode: data.code, jewelryName: '', purity: '',
            grossWeight: '', netWeight: '', stoneWeight: '', stoneDetails: []
          })
          return
        }
        const categoryCode = code.trim().toUpperCase().split('-')[0]
        const catMatch = categories.find(c => c.code === categoryCode)
        // Inherit wastage/making from parent if child value is 0
        const parentMatch = catMatch?.parent_id ? categories.find(c => c.id === catMatch.parent_id) : null
        const effectiveWastage = catMatch
          ? (parseFloat(catMatch.wastage_charges) > 0 ? catMatch.wastage_charges : (parentMatch?.wastage_charges || ''))
          : ''
        const effectiveMaking = catMatch
          ? (parseFloat(catMatch.making_charges) > 0 ? catMatch.making_charges : (parentMatch?.making_charges || ''))
          : ''
        const wp = effectiveWastage !== '' ? String(effectiveWastage) : item.wastagePercent
        const mc = effectiveMaking  !== '' ? String(effectiveMaking)  : item.makingCharge
        // Certification charge per carat — same parent fallback as wastage/making
        const certPerCarat = catMatch
          ? (parseFloat(catMatch.certification_charges) > 0 ? parseFloat(catMatch.certification_charges) : (parseFloat(parentMatch?.certification_charges) || 0))
          : 0

        const stones = data.stones || []
        // Total diamond carats — only diamonds attract a certification charge
        const diamondCarats = stones
          .filter(s => (s.category || '').toLowerCase() === 'diamond')
          .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0)
        const certificationRequired = data.certificate === 'Yes' && diamondCarats > 0
        // Sum ALL stone categories (already gram-converted by server)
        const stoneWt = Object.values(data.stoneCategoryTotals || {})
          .reduce((sum, cat) => sum + (parseFloat(cat.weight) || 0), 0)
        const gross = parseFloat(data.gross_weight || 0)
        const netWt = Math.max(0, gross - stoneWt).toFixed(3)

        // Stone sale_prices from the API are always in INR — store them as-is.
        // calcItemTotal handles the INR→USD conversion at display time via exchangeRate.

        // Recompute stone total in INR
        const sTotal = stones.reduce((acc, s) =>
          acc + (parseFloat(s.weight || 0)) * (parseFloat(s.sale_price || 0)), 0
        ).toFixed(2)

        onUpdate(item.id, {
          status: 'found', statusMsg: '',
          itemCode: data.code,
          jewelryName: data.description?.trim() || data.code || '', // description, else item code
          purity: String(data.gold_purity || ''),
          grossWeight: String(data.gross_weight || ''),
          netWeight: netWt,
          inventoryNetWeight: data.net_weight || '', // physical net — Cost Panel only
          stoneWeight: String(stoneWt),
          stoneTotal: sTotal,
          stoneDetails: stones,
          wastagePercent: wp,
          makingCharge: mc,
          diamondCarats,
          certChargePerCarat: certPerCarat,
          certificationRequired,
          taxRate: String(item.currency === 'INR' ? (gstRate || 3) : (usdTaxRate || 0)),
          laborCharges: data.labor_charges ? parseFloat(data.labor_charges) : 0,
          otherCosts: data.other_costs ? parseFloat(data.other_costs) : 0
        })
      } else {
        onUpdate(item.id, { status: 'not_found', statusMsg: '❌ Not found' })
      }
    } catch {
      onUpdate(item.id, { status: 'error', statusMsg: '❌ Lookup failed' })
    }
  }

  const statusBadge = () => {
    if (item.status === 'loading') return <span className="text-xs text-blue-500 flex items-center gap-0.5"><Loader size={9} className="animate-spin" />…</span>
    if (item.status === 'sold') return <span className="text-xs text-red-600 font-semibold">{item.statusMsg}</span>
    if (item.status === 'not_found' || item.status === 'error') return <span className="text-xs text-orange-500">{item.statusMsg}</span>
    return null
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border mb-2 overflow-hidden ${item.selected ? 'border-slate-400 ring-1 ring-slate-300' : 'border-slate-200'}`}>

      {/* ── Compact header ── */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-100 border-b border-slate-200 flex-wrap">
        <button onClick={() => onUpdate(item.id, { selected: !item.selected })} className="text-slate-600 flex-shrink-0">
          {item.selected ? <CheckSquare size={14} /> : <Square size={14} className="text-slate-400" />}
        </button>
        <span className="text-xs text-slate-400 flex-shrink-0">#{index + 1}</span>

        <select
          value={item.currency}
          onChange={e => onUpdate(item.id, { currency: e.target.value })}
          className="border border-slate-300 rounded px-1 py-0.5 text-xs flex-shrink-0 focus:outline-none focus:ring-1 focus:ring-slate-400 font-semibold bg-white"
        >
          <option value="USD">�� USD</option>
          <option value="INR">�� INR</option>
        </select>

        <div className="flex flex-col flex-shrink-0">
          <input
            ref={itemCodeRef}
            type="text"
            value={item.itemCode}
            onChange={e => onUpdate(item.id, { itemCode: e.target.value.toUpperCase(), status: 'idle', statusMsg: '' })}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchDetails(item.itemCode) } }}
            onBlur={() => { if (item.itemCode && item.status === 'idle') fetchDetails(item.itemCode) }}
            placeholder="Item Code"
            className={`border rounded px-2 py-0.5 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-slate-400 ${item.status === 'sold' ? 'border-red-400 bg-red-50' : item.status === 'not_found' || item.status === 'error' ? 'border-orange-400' : ''}`}
          />
          {statusBadge()}
        </div>

        <input
          type="text"
          value={item.jewelryName}
          onChange={e => onUpdate(item.id, { jewelryName: e.target.value })}
          placeholder="Description"
          className="border rounded px-2 py-0.5 text-xs flex-1 min-w-28 focus:outline-none focus:ring-1 focus:ring-slate-400"
        />

        <span className="ml-auto text-sm font-bold text-slate-800 flex-shrink-0">{fmtCurrency(computed.total, item.currency)}</span>

        <button onClick={() => setExpanded(current => {
          const next = !current
          onCostActivate(item.id, next)
          return next
        })} className="text-gray-400 hover:text-gray-700 p-0.5 rounded hover:bg-gray-200 flex-shrink-0">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <button onClick={() => onRemove(item.id)} className="text-red-400 hover:text-red-600 p-0.5 rounded hover:bg-red-50 flex-shrink-0">
          <Trash2 size={14} />
        </button>
      </div>

      {/* ── Expanded panel ── */}
      {expanded && (
        <div
          className="px-3 py-3 space-y-2 bg-slate-50/60 border-t border-slate-100"
          onMouseEnter={() => { if (!isCostActive) onCostActivate(item.id, true) }}
        >

          {/* Line 1: Gross Wt | Stone Wt | Net Wt | Purity | Gold Rate | Gold Value */}
          <div className="grid grid-cols-6 gap-1.5">
            <div>
              <div className={lblClass}>Gross Wt (g)</div>
              <input type="text" value={item.grossWeight || '—'} readOnly className={roClass} />
            </div>
            <div>
              <div className={lblClass}>Stone Wt (g)</div>
              <input type="text" value={computed.stoneWeight !== '0.000' ? computed.stoneWeight : (item.stoneWeight || '—')} readOnly className={roClass} />
            </div>
            <div>
              <div className={lblClass}>Net Wt (g)</div>
              <input type="text" value={computed.netWeight || '—'} readOnly className={`${roClass} font-semibold`} />
            </div>
            <div>
              <div className={lblClass}>Purity</div>
              <input type="text" value={item.purity ? `${item.purity}K` : '—'} readOnly className={`${roClass} text-center font-semibold`} />
            </div>
            <div>
              <div className={lblClass}>Gold Rate ({sym}/g)</div>
              <input type="text" value={
                item.goldRate
                  ? (item.currency === 'USD' && exchangeRate
                      ? `${sym}${(parseFloat(item.goldRate) / exchangeRate).toFixed(2)}`
                      : `${sym}${parseFloat(item.goldRate).toLocaleString()}`)
                  : '—'
              } readOnly className={roClass} />
            </div>
            <div>
              <div className={lblClass}>Gold Value</div>
              <input type="text" value={`${sym}${parseFloat(computed.goldValue).toLocaleString()}`} readOnly className={roClass} />
            </div>
          </div>

          {/* Line 2: Wastage % (edit) | Wastage Amt (RO) | Making /g (edit) | Making Charges (RO) */}
          <div className="grid grid-cols-4 gap-1.5">
            <div>
              <div className={lblClass}>Wastage %</div>
              <input type="number" value={item.wastagePercent}
                onChange={e => onUpdate(item.id, { wastagePercent: e.target.value })}
                onKeyDown={blurOnEnter} placeholder="0"
                className={edClass} />
            </div>
            <div>
              <div className={lblClass}>Wastage Amt</div>
              <input type="text" value={`${sym}${parseFloat(computed.wastageAmt).toLocaleString()}`} readOnly className={roClass} />
            </div>
            <div>
              <div className={lblClass}>Making /g (₹ INR)</div>
              <input type="number" value={item.makingCharge}
                onChange={e => onUpdate(item.id, { makingCharge: e.target.value })}
                onKeyDown={blurOnEnter} placeholder="0"
                className={edClass} />
            </div>
            <div>
              <div className={lblClass}>Making Charges</div>
              <input type="text" value={`${sym}${parseFloat(computed.makingAmt).toLocaleString()}`} readOnly className={roClass} />
            </div>
          </div>

          {/* Line 3: Stone Details Table */}
          {item.stoneDetails && item.stoneDetails.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Stone Details</div>
              <table className="w-full text-xs border rounded">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-2 py-1 border-b font-medium">Code</th>
                    <th className="text-left px-2 py-1 border-b font-medium">Name</th>
                    <th className="text-right px-2 py-1 border-b font-medium">Weight</th>
                    <th className="text-left px-2 py-1 border-b font-medium">Unit</th>
                    <th className="text-right px-2 py-1 border-b font-medium">Rate (₹ INR)</th>
                    <th className="text-right px-2 py-1 border-b font-medium">
                      Value{item.currency === 'USD' ? ' ($ / ₹)' : ' (₹)'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.stoneDetails.map((s, i) => {
                    const unit = stoneUnitLabel(s)
                    const w = parseFloat(s.weight || 0)
                    const rINR = parseFloat(s.sale_price || s.rate || 0)
                    const exRate = parseFloat(exchangeRate) || 1
                    const valueINR = w * rINR
                    const valueUSD = valueINR / exRate
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1">{s.stone_code || s.code || '—'}</td>
                        <td className="px-2 py-1">{s.stone_name || s.name || '—'}</td>
                        <td className="px-2 py-1 text-right">{w.toFixed(3)}</td>
                        <td className="px-2 py-1">{unit}</td>
                        <td className="px-1 py-0.5 text-right">
                          <input
                            type="number"
                            value={s.sale_price ?? s.rate ?? ''}
                            onChange={e => handleStoneRateChange(i, e.target.value)}
                            onKeyDown={blurOnEnter}
                            className="w-20 border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </td>
                        <td className="px-2 py-1 text-right font-medium whitespace-nowrap">
                          {item.currency === 'USD' ? (
                            <>
                              ${valueUSD.toFixed(2)}
                              <span className="text-slate-400 font-normal"> / ₹{valueINR.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </>
                          ) : (
                            <>₹{valueINR.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Certification charge — only shown when it applies (diamond item, cert required) */}
          {parseFloat(computed.certAmt) > 0 && (
            <div className="grid grid-cols-4 gap-1.5">
              <div>
                <div className={lblClass}>Cert Charges</div>
                <input type="text"
                  value={`${sym}${parseFloat(computed.certAmt).toLocaleString()}`}
                  readOnly className={roClass} />
              </div>
              <div className="col-span-3 flex items-end">
                <span className="text-[10px] text-gray-400 pb-1">
                  {parseFloat(item.diamondCarats || 0).toFixed(3)} ct × ₹{parseFloat(item.certChargePerCarat || 0).toLocaleString()}/ct
                </span>
              </div>
            </div>
          )}

          {/* Line 4: Total Stone Value (RO) | Tax % (edit) | Tax Amount (RO) | Grand Total (RO) */}
          <div className="grid grid-cols-4 gap-1.5">
            <div>
              <div className={lblClass}>Total Stone Value</div>
              <input type="text"
                value={item.currency === 'USD'
                  ? `$${parseFloat(computed.stoneTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} / ₹${parseFloat(computed.stoneTotalINR || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                  : `₹${parseFloat(computed.stoneTotalINR || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                readOnly className={roClass} />
            </div>
            <div>
              <div className={lblClass}>
                Tax {item.currency === 'INR' ? `(GST ${item.taxRate || gstRate}%)` : `(${item.taxRate || usdTaxRate}%)`}
              </div>
              <input
                type="number"
                value={item.taxRate || (item.currency === 'INR' ? gstRate : usdTaxRate) || ''}
                onChange={e => onUpdate(item.id, { taxRate: e.target.value })}
                placeholder="0"
                className={edClass}
              />
            </div>
            <div>
              <div className={lblClass}>Tax Amount</div>
              <input type="text" value={`${sym}${parseFloat(computed.taxAmt).toLocaleString()}`} readOnly className={roClass} />
            </div>
            <div>
              <div className={`${lblClass} font-semibold text-slate-600`}>Grand Total</div>
              <input type="text" value={fmtCurrency(computed.total, item.currency)} readOnly
                className="w-full rounded px-2 py-1.5 text-xs bg-slate-800 font-bold text-white text-right" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className={lblClass}>Notes</div>
            <input type="text" value={item.notes}
              onChange={e => onUpdate(item.id, { notes: e.target.value })}
              placeholder="Optional notes"
              className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>

          {/* ── HOLD-TO-PEEK COST PANEL (print:hidden, admin/manager only) ── */}
          {showCostPanel && (() => {
            const categoryCode = item.itemCode ? item.itemCode.trim().toUpperCase().split('-')[0] : null
            const catMatch = categoryCode ? categories.find(c => c.code === categoryCode) : null
            const parentMatch = catMatch?.parent_id ? categories.find(c => c.id === catMatch.parent_id) : null
            const catInfo = catMatch || parentMatch

            if (!catInfo) return null

            const nw = parseFloat(item.inventoryNetWeight) || parseFloat(computed.netWeight) || 0 // Cost Panel: physical inventory Net Wt
            const goldRateInr = parseFloat(item.goldRate) || 0
            const purityFactor = getGoldPurityFactor(item.purity)
            const goldCostINRRaw = nw * goldRateInr * purityFactor
            const goldCostINR = item.currency === 'USD' ? applyKundanGoldCostDeduction(goldCostINRRaw, item.itemCode) : goldCostINRRaw // Kundan: −6% (US only)
            const stoneCostINR = (item.stoneDetails && item.stoneDetails.length > 0)
              ? item.stoneDetails.reduce((acc, s) => acc + (parseFloat(s.weight) || 0) * (parseFloat(s.sale_price ?? s.rate) || 0), 0)
              : parseFloat(item.stoneTotal) || 0

            const laborCostINR = Math.ceil(nw * (parseFloat(item.laborCharges) || 0))
            const preTaxCostINR = calculateAcquisitionCost({ goldCost: goldCostINR, stoneCost: stoneCostINR, laborCost: laborCostINR, otherCost: item.otherCosts })
            const itemTaxRate = parseFloat(item.taxRate) || 0
            const taxAmtINR  = Math.ceil((preTaxCostINR * itemTaxRate) / 100)
            const quotedCostINR = item.currency === 'USD'
              ? parseFloat(computed.total) * (parseFloat(exchangeRate) || 1)
              : parseFloat(computed.total) || 0
            const { actualCost: actualCostINR, profit, profitPct } = calculateCostPanelResult({ preTaxCost: preTaxCostINR, taxAmount: taxAmtINR, quotedCost: quotedCostINR })

            return (
              <CostPanel
                title={`#${index + 1} · ${item.itemCode || item.jewelryName || 'Item'}`}
                textSizeClassName="text-xs"
                rows={[
                  { label: item.currency === 'USD' && isKundanItemCode(item.itemCode) ? 'Gold Cost (Kundan −6%)' : 'Gold Cost', value: Math.ceil(goldCostINR) },
                  { label: 'Stones Cost', value: Math.ceil(stoneCostINR) },
                  { label: `Labor${item.laborCharges ? ` (₹${item.laborCharges}/g)` : ''}`, value: Math.ceil(laborCostINR) },
                  { label: 'Other Costs', value: parseFloat(item.otherCosts) || 0 },
                ]}
                quotedCost={quotedCostINR}
                actualCost={actualCostINR}
                profit={profit}
                profitPct={profitPct}
              />
            )
          })()}

        </div>
      )}
    </div>
  )
}

// ─── EstimateCart (main component) ─────────────────────────────────────────

const EstimateCart = () => {
  // ── Customer info ──
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [estimateDate, setEstimateDate] = useState(todayStr())
  const [cartNotes, setCartNotes] = useState('')

  // ── Cart items ──
  const [items, setItems] = useState([newCartItem()])

  // ── Rates ──
  const [goldRateINR, setGoldRateINR] = useState('')
  const [exchangeRate, setExchangeRate] = useState(null)   // Rate-002: INR per 1 USD
  const [gstRate, setGstRate] = useState(3)                // Rate-003: GST % for INR items (gst_percentage)
  const [usdTaxRate, setUsdTaxRate] = useState(0)          // Rate-003: Tax % for USD items (customs_duty + state_tax)
  const [categories, setCategories] = useState([])
  const [rateMsg, setRateMsg] = useState('')

  // ── UI state ──
  const [converting, setConverting] = useState(false)
  const [convertMsg, setConvertMsg] = useState('')
  const [showLoadModal, setShowLoadModal] = useState(false)
  const [pdfViewUrl, setPdfViewUrl] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [activeCostItemId, setActiveCostItemId] = useState(null)

  const handleCostActivate = useCallback((itemId, active) => {
    setActiveCostItemId(current => active ? itemId : (current === itemId ? null : current))
  }, [])

  // ── Fetch rates & categories on mount ──
  useEffect(() => {
    const fetchGold = fetch('/api/rates/gold/today', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.rate) {
          const r24 = parseFloat(data.rate.gold_24k)
          setGoldRateINR(r24.toFixed(2))
          return r24
        } else {
          setRateMsg('Gold rate not found. Enter manually per item.')
          return null
        }
      })
      .catch(() => { setRateMsg('Could not fetch gold rate. Enter manually per item.'); return null })

    const fetchDollar = fetch('/api/rates/dollar/today', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.rate) {
          const exRate = applyExchangeRate(data.rate.usd_to_inr)
          setExchangeRate(exRate)
          return exRate
        }
        return null
      })
      .catch(() => null)

    // Compute USD gold rate = INR gold rate / exchange rate (reserved for future use)
    Promise.all([fetchGold, fetchDollar]).then(([_r24, _exRate]) => {
      // USD rate computation removed — was unused
    })

    fetch('/api/categories', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setCategories(data) })
      .catch(() => {})

    // Rate-003: fetch tax rates — INR uses gst_percentage, USD uses customs_duty + state_tax
    fetch('/api/rates/tax/latest', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.rate) {
          if (data.rate.gst_percentage != null) {
            setGstRate(parseFloat(data.rate.gst_percentage))
          }
          const customsDuty = parseFloat(data.rate.customs_duty) || 0
          const stateTax    = parseFloat(data.rate.state_tax)    || 0
          setUsdTaxRate(customsDuty + stateTax)
        }
      })
      .catch(() => {})
  }, [])

  const handleEstimateDateChange = async (event) => {
    const selectedDate = event.target.value
    setEstimateDate(selectedDate)
    if (!selectedDate) return

    try {
      const { gold, dollar } = await HistoricalRateService.getRates(selectedDate)
      const missing = []
      if (gold?.gold_24k != null && gold.gold_24k !== '') {
        const historicalGoldRate = parseFloat(gold.gold_24k).toFixed(2)
        setGoldRateINR(historicalGoldRate)
        setItems(currentItems => currentItems.map(item => ({
          ...item,
          goldRate: historicalGoldRate
        })))
      } else {
        missing.push('gold')
      }
      if (dollar?.usd_to_inr != null && dollar.usd_to_inr !== '') {
        setExchangeRate(applyExchangeRate(parseFloat(dollar.usd_to_inr)))
      } else {
        missing.push('dollar')
      }
      setRateMsg(missing.length
        ? `No ${missing.join(' or ')} rate saved for ${selectedDate}. Existing values were kept.`
        : `Gold and dollar rates updated for ${selectedDate}.`)
    } catch (error) {
      setRateMsg(error.message || 'Could not fetch rates for selected date.')
    }
  }

  // ── Cart operations ──
  const addItem = () => {
    const item = newCartItem()
    // Pre-fill gold rate and tax rate with current fetched values
    item.goldRate = String(goldRateINR)
    item.taxRate = String(item.currency === 'INR' ? (gstRate || 3) : (usdTaxRate || 0))
    setItems(prev => [...prev, item])
  }

  const removeItem = (id) => {
    setItems(prev => prev.filter(i => i.id !== id))
  }

  const updateItem = (id, patch) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i))
  }

  const clearCart = () => {
    if (!window.confirm('Clear all items and customer info?')) return
    setItems([newCartItem()])
    setCustomerName('')
    setCustomerPhone('')
    setCustomerEmail('')
    setCartNotes('')
    setConvertMsg('')
  }

  // ── Totals ──
  const totals = items.reduce((acc, item) => {
    const c = calcItemTotal(item, exchangeRate, gstRate, usdTaxRate)
    const cur = item.currency
    acc[cur] = (acc[cur] || 0) + parseFloat(c.total)
    return acc
  }, {})

  const selectedItems = items.filter(i => i.selected)
  const selectedTotals = selectedItems.reduce((acc, item) => {
    const c = calcItemTotal(item, exchangeRate, gstRate, usdTaxRate)
    const cur = item.currency
    acc[cur] = (acc[cur] || 0) + parseFloat(c.total)
    return acc
  }, {})

  const selectAll = () => setItems(prev => prev.map(i => ({ ...i, selected: true })))
  const deselectAll = () => setItems(prev => prev.map(i => ({ ...i, selected: false })))

  // Single currency shared by every selected item, or null when the selection
  // is empty or mixes INR + USD (a bill — and therefore a checkout — is
  // single-currency).
  const selectedCurrency = (() => {
    const set = new Set(selectedItems.map(i => i.currency))
    return set.size === 1 ? [...set][0] : null
  })()
  const checkoutTotal = selectedCurrency ? (selectedTotals[selectedCurrency] || 0) : 0

  // Map a group of cart items to billing_items payload rows. All money fields
  // are INR; the server converts the USD grand total with exchange_rate.
  const buildLineItems = (group) => group.map(item => {
    const c = calcItemTotal(item, exchangeRate, gstRate, usdTaxRate)
    return {
      item_code: item.itemCode || null,
      purity: item.purity || null,
      gross_weight: parseFloat(item.grossWeight) || null,
      net_weight: parseFloat(item.netWeight) || parseFloat(c.fineWeight) || null,
      stone_weight: parseFloat(item.stoneWeight) || null,
      gold_rate: parseFloat(item.goldRate) || null,
      wastage_amount: parseFloat(c.wastageAmtINR) || 0,
      making_amount: parseFloat(c.makingAmtINR) || 0,
      stone_total: parseFloat(c.stoneTotalINR) || 0,
      cert_charges: parseFloat(c.certAmtINR) || 0,
      tax_amount: parseFloat(c.taxAmtINR) || 0,
      item_total: parseFloat(c.grandTotalINR),
      stones: item.stoneDetails || [],
    }
  })

  // Invoice-PDF / preview payload built from the selected cart lines of one
  // currency — mirrors BillDetailModal.buildMultiItemInvoiceData but sourced
  // from the live cart instead of a saved bill.
  const buildCartInvoiceData = (currency, billNumber = '') => {
    const group = selectedItems.filter(i => i.currency === currency)
    const per = group.map(item => ({ item, c: calcItemTotal(item, exchangeRate, gstRate, usdTaxRate) }))
    const grandINR = per.reduce((s, { c }) => s + parseFloat(c.grandTotalINR), 0)
    const customsINR = per.reduce((s, { c }) => s + parseFloat(c.taxAmtINR), 0)
    const baseINR = grandINR - customsINR
    const isUSD = currency === 'USD'
    return {
      currency,
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      date: estimateDate,
      billNumber,
      estimateNumber: billNumber,
      customerName,
      customerPhone,
      customerEmail,
      exchangeRate: isUSD ? (parseFloat(exchangeRate) || 0) : 0,
      baseTotalINR: baseINR.toFixed(2),
      customsINR: customsINR.toFixed(2),
      customsPercent: isUSD ? (parseFloat(usdTaxRate) || null) : (parseFloat(gstRate) || null),
      grandTotalINR: grandINR.toFixed(2),
      grandTotal: grandINR.toFixed(2),
      grandTotalUSD: isUSD && parseFloat(exchangeRate) > 0 ? (grandINR / parseFloat(exchangeRate)).toFixed(2) : undefined,
      items: per.map(({ item, c }) => ({
        itemCode: item.itemCode || '',
        purity: item.purity || '',
        grossWeight: item.grossWeight,
        netWeight: c.netWeight,
        stoneWeight: item.stoneWeight,
        stones: item.stoneDetails || [],
        amountINR: parseFloat(c.grandTotalINR).toFixed(2),
      })),
      notes: cartNotes || '',
    }
  }

  const previewBillNumber = async (currency) => {
    try {
      const res = await fetch(`/api/billings/next-number/${currency}`, { credentials: 'include' })
      const data = await res.json()
      return data.success ? (data.data.billNumber || '') : ''
    } catch { return '' }
  }

  const checkoutGuard = () => {
    if (!customerName.trim()) { alert('Please enter customer name'); return false }
    if (selectedItems.length === 0) { alert('Select at least one item'); return false }
    if (!selectedCurrency) { alert('Select items of a single currency — a bill can’t mix INR and USD. Do one currency, then the other.'); return false }
    const sold = selectedItems.filter(i => i.status === 'sold')
    if (sold.length > 0) { alert(`Cannot proceed — already sold: ${sold.map(i => i.itemCode).join(', ')}`); return false }
    return true
  }

  // ── View — open the invoice PDF preview for the selected lines ──
  const handleView = async () => {
    if (!checkoutGuard()) return
    try {
      const num = await previewBillNumber(selectedCurrency)
      const url = await PdfService.getMultiItemInvoicePdfUrl(buildCartInvoiceData(selectedCurrency, num), selectedCurrency)
      setPdfViewUrl(url)
    } catch (err) {
      alert('Could not open preview: ' + err.message)
    }
  }

  // ── Invoice PDF — download it ──
  const handleInvoicePdf = async () => {
    if (!checkoutGuard()) return
    try {
      const num = await previewBillNumber(selectedCurrency)
      await PdfService.downloadMultiItemInvoicePDF(buildCartInvoiceData(selectedCurrency, num), selectedCurrency)
    } catch (err) {
      alert('Could not generate PDF: ' + err.message)
    }
  }

  // ── Checkout — open the shared payment modal ──
  const handleCheckout = () => {
    if (!checkoutGuard()) return
    if (checkoutTotal <= 0) { alert('Grand total is zero — nothing to bill'); return }
    setShowPaymentModal(true)
  }

  // Called by PaymentConfirmModal: create the (single-currency) multi-item bill,
  // then record each payment against it. Returns the saved bill or null.
  const savePaidBill = async (payments) => {
    const currency = selectedCurrency
    const group = selectedItems.filter(i => i.currency === currency)
    setConverting(true)
    try {
      const res = await fetch('/api/billings/multi-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_name: customerName,
          customer_phone: customerPhone,
          customer_email: customerEmail,
          currency,
          exchange_rate: currency === 'USD' ? (parseFloat(exchangeRate) || null) : null,
          notes: cartNotes || null,
          items: buildLineItems(group),
        }),
      })
      const data = await res.json()
      if (!data.success) { alert(data.error || data.message || 'Failed to create bill'); return null }
      const bill = data.data

      const valid = (payments || []).filter(p => parseFloat(p.amount) > 0)
      const failed = []
      for (const p of valid) {
        try {
          const payRes = await fetch(`/api/billings/${bill.id}/payments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              payment_type: p.payment_type,
              amount: parseFloat(p.amount),
              reference_number: p.reference_number || null,
              payment_date: p.payment_date || null,
              notes: p.notes || null,
              old_gold_items: p.old_gold_items || [],
            }),
          })
          const payData = await payRes.json().catch(() => ({}))
          if (!payRes.ok || !payData.success) failed.push(`${p.payment_type} ${p.amount}: ${payData.error || payRes.status}`)
        } catch (err) {
          failed.push(`${p.payment_type} ${p.amount}: ${err.message}`)
        }
      }
      if (failed.length > 0) {
        alert(`Bill ${bill.bill_number} was created, but these payments could not be recorded:\n\n${failed.join('\n')}\n\nThe bill will show as partially paid — add the missing payments from Billing History.`)
      }

      // Mark each loaded line's source estimate as converted — mirrors what
      // US/Indian Billing already do once checkout completes.
      group.forEach(item => {
        if (item.estimateId) {
          fetch(`/api/estimates/${item.estimateId}/convert`, { method: 'POST', credentials: 'include' })
            .catch(err => console.warn('Could not mark estimate as converted:', err))
        }
      })

      group.forEach(item => updateItem(item.id, { selected: false }))
      setConvertMsg(`✅ Bill ${bill.bill_number} created`)
      setTimeout(() => setConvertMsg(''), 8000)
      return bill
    } catch (err) {
      alert('Failed to create bill: ' + err.message)
      return null
    } finally {
      setConverting(false)
    }
  }

  // Receipt senders for the payment modal's confirmation step.
  const receiptDataFor = (bill) => {
    const its = Array.isArray(bill.items) ? bill.items : []
    const base = parseFloat(bill.base_total) || 0
    const customs = parseFloat(bill.customs_amount) || 0
    const grandUSD = parseFloat(bill.grand_total) || 0
    const grandINR = base + customs
    const isUSD = bill.currency === 'USD'
    return {
      currency: bill.currency,
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      date: bill.created_at,
      billNumber: bill.bill_number,
      estimateNumber: bill.bill_number,
      customerName: bill.customer_name,
      customerPhone: bill.customer_phone,
      customerEmail: bill.customer_email,
      exchangeRate: isUSD && grandUSD > 0 ? grandINR / grandUSD : 0,
      baseTotalINR: base.toFixed(2),
      customsINR: customs.toFixed(2),
      customsPercent: parseFloat(bill.customs_percent) || null,
      grandTotalINR: grandINR.toFixed(2),
      grandTotal: grandINR.toFixed(2),
      grandTotalUSD: isUSD ? grandUSD.toFixed(2) : undefined,
      items: its.map(it => ({
        itemCode: it.item_code || '',
        purity: it.purity || '',
        grossWeight: it.gross_weight,
        netWeight: it.net_weight,
        stoneWeight: it.stone_weight,
        stones: Array.isArray(it.stones) ? it.stones : [],
        amountINR: (parseFloat(it.item_total) || 0).toFixed(2),
      })),
      notes: bill.notes || '',
    }
  }
  const sendReceiptEmail = async (bill) => {
    if (!bill?.customer_email) return
    await EmailSendService.sendEmail(receiptDataFor(bill), bill.customer_email)
  }
  const sendReceiptWhatsApp = async (bill) => {
    if (!bill?.customer_phone) return
    const its = Array.isArray(bill.items) ? bill.items : []
    await WhatsAppLinkService.shareInvoice({
      customerName: bill.customer_name || 'Customer',
      phoneNumber: bill.customer_phone,
      date: bill.created_at,
      billNumber: bill.bill_number,
      currency: bill.currency,
      grandTotal: (parseFloat(bill.grand_total) || 0).toFixed(2),
      companyName: 'GDS Jewellers',
      companyAddress: '15520 Pawnee St, Overland Park, KS - 66224',
      items: its.map(it => ({
        itemCode: it.item_code || '—',
        purity: it.purity ? `${it.purity}K` : '',
        netWeight: parseFloat(it.net_weight || 0).toFixed(3),
        amount: (parseFloat(it.item_total) || 0).toFixed(2),
      })),
    }, bill.customer_phone)
  }

  // ── Load estimate into cart ──
  // A bill is customer-scoped — every line must belong to the same customer.
  // Before any real item is loaded, an estimate's customer info is adopted
  // outright (it may correct a name typed by mistake); once the cart has a
  // real item, a different customer's estimate is refused rather than
  // silently mixed in under the first customer's name.
  const handleLoadEstimate = (item, customerInfo) => {
    const hasRealItems = items.some(i => i.itemCode || i.jewelryName)
    const incomingName = (customerInfo.name || '').trim()

    if (hasRealItems && customerName.trim() && incomingName &&
        incomingName.toLowerCase() !== customerName.trim().toLowerCase()) {
      alert(
        `This estimate belongs to ${incomingName}, but the cart already has item(s) for ${customerName}.\n\n` +
        `A bill covers one customer only. Finish or clear this cart first, or search for another estimate for ${customerName}.`
      )
      return
    }

    if (!hasRealItems) {
      if (incomingName) setCustomerName(incomingName)
      if (customerInfo.phone) setCustomerPhone(customerInfo.phone)
      if (customerInfo.email) setCustomerEmail(customerInfo.email)
    } else {
      if (customerInfo.phone && !customerPhone) setCustomerPhone(customerInfo.phone)
      if (customerInfo.email && !customerEmail) setCustomerEmail(customerInfo.email)
    }

    // Auto-fill gold rate (always INR regardless of display currency)
    item.goldRate = item.goldRate || String(goldRateINR)
    setItems(prev => {
      // Replace empty first item if it's blank
      if (prev.length === 1 && !prev[0].itemCode && !prev[0].jewelryName) return [item]
      return [...prev, item]
    })
    setShowLoadModal(false)
  }

  return (
    <div className="pb-40 min-h-screen bg-slate-50">
      <PageIdentifier pageId={SCREEN_IDS?.BILLING?.MULTI_ITEM_BILLING || 'BILL-006'} pageName="Multi-Item Billing" />

      <BillingActionBar
        title="Billing"
        subtitle="multi-item · one currency per bill"
        onLoadEstimate={() => setShowLoadModal(true)}
        onClear={clearCart}
        onView={handleView}
        onInvoicePdf={handleInvoicePdf}
        onCheckout={handleCheckout}
        viewDisabled={!selectedCurrency || converting}
        invoiceDisabled={!selectedCurrency || converting}
        checkoutDisabled={!selectedCurrency || converting || checkoutTotal <= 0}
        checkoutLabel={selectedItems.length > 0 ? `Checkout (${selectedItems.length})` : 'Checkout'}
        goldRate={goldRateINR || null}
        usdRate={exchangeRate || null}
        gstRate={gstRate}
      />

      <div className="p-4 max-w-6xl mx-auto">

      {rateMsg && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-xs text-amber-700">
          <AlertTriangle size={13} /> {rateMsg}
        </div>
      )}

      {/* ── Customer Info ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-4">
        <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
          <User size={13} className="text-slate-300" />
          <span className="text-white font-semibold text-xs tracking-widest uppercase">Customer Information</span>
        </div>
        <div className="p-3">
        <h2 className="hidden">
          <User size={14} /> Customer Information
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Bill Date</label>
            <input type="date" value={estimateDate} onChange={handleEstimateDateChange}
              className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Customer Name *</label>
            <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
              onKeyDown={blurOnEnter} placeholder="Required"
              className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Phone</label>
            <input type="text" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)}
              onKeyDown={blurOnEnter} placeholder="Phone number"
              className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Email</label>
            <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
              onKeyDown={blurOnEnter} placeholder="Email"
              className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
            <input type="text" value={cartNotes} onChange={e => setCartNotes(e.target.value)}
              placeholder="Optional"
              className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>
        </div>
        </div>
      </div>

      {/* ── Cart Items ── */}
      <div className="mb-3">
        {/* Cart toolbar */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">Items ({items.length})</span>
            <button onClick={selectAll} className="text-xs font-semibold text-slate-600 hover:text-slate-800 underline">Select All</button>
            <button onClick={deselectAll} className="text-xs text-slate-400 hover:text-slate-600 underline">Deselect All</button>
            <span className="text-xs text-slate-400">({selectedItems.length} selected for conversion)</span>
          </div>
        </div>

        {/* Item rows */}
        {items.map((item, index) => (
          <CartRow
            key={item.id}
            item={item}
            index={index}
            onUpdate={updateItem}
            onRemove={removeItem}
            goldRateINR={goldRateINR}
            exchangeRate={exchangeRate}
            gstRate={gstRate}
            usdTaxRate={usdTaxRate}
            categories={categories}
            isCostActive={activeCostItemId === item.id}
            onCostActivate={handleCostActivate}
          />
        ))}

        {/* Add item button */}
        <button
          onClick={addItem}
          className="w-full py-2 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 text-sm hover:bg-slate-100 hover:border-slate-400 transition flex items-center justify-center gap-2"
        >
          <Plus size={16} /> Add Item
        </button>
      </div>

      {/* ── Totals Summary ── */}
      {items.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-4">
          <div className="bg-slate-600 px-4 py-2 flex items-center gap-2">
            <span className="text-white font-semibold text-xs tracking-widest uppercase">Cart Totals</span>
            <span className="ml-auto text-[11px] text-slate-300">
              {items.length} item{items.length !== 1 ? 's' : ''}
              {Object.keys(totals).length > 1 ? ` · ${Object.keys(totals).join(' + ')}` : ''}
            </span>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-8 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">Cart Total</div>
              <div className="flex gap-4">
                {totals.INR ? <span className="text-lg font-bold text-slate-800">₹{totals.INR.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span> : null}
                {totals.USD ? <span className="text-lg font-bold text-green-700">${totals.USD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> : null}
              </div>
            </div>
            {(selectedTotals.INR || selectedTotals.USD) && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">Selected for Conversion</div>
                <div className="flex gap-4">
                  {selectedTotals.INR ? <span className="text-base font-semibold text-slate-600">₹{selectedTotals.INR.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span> : null}
                  {selectedTotals.USD ? <span className="text-base font-semibold text-green-600">${selectedTotals.USD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> : null}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Action Messages ── */}
      {convertMsg && (
        <div className={`flex items-center gap-2 rounded px-3 py-2 mb-3 text-sm ${convertMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-orange-50 text-orange-700 border border-orange-200'}`}>
          {convertMsg.startsWith('✅') ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          {convertMsg}
        </div>
      )}

      </div>

      {/* ── Load Estimate Modal ── */}
      {showLoadModal && (
        <LoadEstimateModal
          lockedCustomer={items.some(i => i.itemCode || i.jewelryName) ? customerName : ''}
          onClose={() => setShowLoadModal(false)}
          onLoad={handleLoadEstimate}
        />
      )}

      {/* ── Invoice preview ── */}
      {pdfViewUrl && (
        <PdfViewerModal
          url={pdfViewUrl}
          title="Invoice Preview"
          onClose={() => { URL.revokeObjectURL(pdfViewUrl); setPdfViewUrl(null) }}
        />
      )}

      {/* ── Checkout / payment ── */}
      <PaymentConfirmModal
        isOpen={showPaymentModal}
        onClose={(wasSaved) => {
          setShowPaymentModal(false)
          if (wasSaved) setItems(prev => prev.filter(i => !i.selected).length ? prev.filter(i => !i.selected) : [newCartItem()])
        }}
        grandTotal={checkoutTotal}
        currency={selectedCurrency || 'USD'}
        customerId={null}
        dollarRate={selectedCurrency === 'USD' ? (parseFloat(exchangeRate) || null) : null}
        goldRate={parseFloat(goldRateINR) || null}
        customerName={customerName}
        customerPhone={customerPhone}
        customerEmail={customerEmail}
        onSaveAndConfirm={savePaidBill}
        onSendEmail={sendReceiptEmail}
        onSendWhatsApp={sendReceiptWhatsApp}
      />
    </div>
  )
}

export default EstimateCart
